import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, CodeBlock, Icon } from "@sagot/ui";

const K = "deployKnowledge.widgetStudio.install";
const NOTES = ["src", "csp", "keep", "reload"] as const;

interface IframeGuideProps {
  /** `SnippetsOut.iframe`: the frame tag, present only while at least one site may frame the chat. */
  iframe: string;
  /** `SnippetsOut.frame_origins`: the sites allowed to frame the chat (Settings > Secure launch). */
  origins: readonly string[];
  /** Origin the frame loads the chat from: the operator's CSP needs a `frame-src` for it. */
  chatOrigin: string;
  settingsHref: string;
}

/**
 * Install > Iframe (mockups/privacy-security.html pin 6, HCU-006): the exact frame tag and how many sites may show the chat.
 * The frame opens the operator's own launch endpoint, which redirects to `<chat>/launch#code=...`.
 */
export function IframeGuide({ iframe, origins, chatOrigin, settingsHref }: IframeGuideProps) {
  const { t } = useTranslation();
  const copy = { copyLabel: t("common.copy"), copiedLabel: t("common.copied") };
  return (
    <div className="space-y-4" data-testid="iframe-guide">
      <p className="text-xs text-fg-muted">{t(`${K}.iframeIntro`)}</p>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="iframe-origins">
        <span className="inline-flex items-center gap-1.5 rounded-pill bg-primary-soft px-2.5 py-1 font-semibold text-primary-ink">
          <Icon name="shield-check" className="size-3.5" />
          {t(`${K}.iframeAllowed`, { count: origins.length })}
        </span>
        {origins.map((origin) => (
          <code key={origin} className="rounded-md bg-surface-2 px-2 py-1 font-mono text-fg-muted">
            {origin}
          </code>
        ))}
      </div>
      <CodeBlock code={iframe} label={t(`${K}.snippetLabel`, { method: t(`${K}.iframe`) })} {...copy} />
      <section className="space-y-2" aria-labelledby="iframe-notes-title">
        <h3 id="iframe-notes-title" className="text-sm font-bold">
          {t(`${K}.iframeNotes.title`)}
        </h3>
        <ul className="list-disc space-y-1.5 pl-5 text-xs text-fg-muted" data-testid="iframe-notes">
          {NOTES.map((note) => (
            <li key={note}>{t(`${K}.iframeNotes.${note}`, { origin: chatOrigin })}</li>
          ))}
        </ul>
      </section>
      <Button asChild variant="secondary" size="sm">
        <AppLink href={settingsHref}>
          <Icon name="settings" />
          {t(`${K}.iframeManage`)}
        </AppLink>
      </Button>
    </div>
  );
}

/** Shown instead of the Iframe method while no site is allowed to frame the chat. */
export function IframeNeedsSite({ settingsHref }: { settingsHref: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-lg border border-line bg-surface-2 p-3.5" data-testid="iframe-needs-origin">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-pill bg-primary-soft text-primary-ink" aria-hidden="true">
        <Icon name="shield-check" className="size-5" />
      </span>
      <div className="min-w-0 flex-1 space-y-2 text-sm">
        <h3 className="font-semibold">{t(`${K}.iframeNeedsTitle`)}</h3>
        <p className="text-xs text-fg-muted">{t(`${K}.iframeNeedsBody`)}</p>
        <Button asChild variant="secondary" size="sm">
          <AppLink href={settingsHref}>
            <Icon name="plus" />
            {t(`${K}.iframeNeedsAction`)}
          </AppLink>
        </Button>
      </div>
    </div>
  );
}
